import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import api from '../services/api';

const ManageAreaItems = () => {
  const [items, setItems] = useState([]);
  const [areas, setAreas] = useState([]);
  const [selectedArea, setSelectedArea] = useState(null);
  const [areaItems, setAreaItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchAreaItems = useCallback(async () => {
    if (!selectedArea) return;
    try {
      const response = await api.get(`/stock-items/area/${selectedArea}`);
      setAreaItems(response.data.items);
    } catch (err) {
      console.error('Failed to load area items:', err);
    }
  }, [selectedArea]);

  useEffect(() => {
    if (selectedArea) {
      fetchAreaItems();
    }
  }, [selectedArea, fetchAreaItems]);

  const fetchData = async () => {
    try {
      const [itemsRes, areasRes] = await Promise.all([
        api.get('/stock-items'),
        api.get('/stock-areas')
      ]);
      setItems(itemsRes.data.items);
      setAreas(areasRes.data.areas);
      if (areasRes.data.areas.length > 0) {
        setSelectedArea(areasRes.data.areas[0].id);
      }
    } catch (err) {
      console.error('Failed to load data:', err);
    }
  };

  const handleAddItemToArea = async (itemId) => {
    try {
      await api.post(`/stock-items/area/${selectedArea}/items`, {
        itemId,
        displayOrder: areaItems.length
      });
      fetchAreaItems();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to add item to area');
    }
  };

  const handleRemoveItemFromArea = async (itemId) => {
    try {
      await api.delete(`/stock-items/area/${selectedArea}/items/${itemId}`);
      fetchAreaItems();
    } catch (err) {
      alert('Failed to remove item from area');
    }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const reorderedItems = Array.from(areaItems);
    const [movedItem] = reorderedItems.splice(result.source.index, 1);
    reorderedItems.splice(result.destination.index, 0, movedItem);

    setAreaItems(reorderedItems);

    // Update order in backend
    try {
      const orderData = reorderedItems.map((item, index) => ({
        id: item.area_item_id,
        displayOrder: index
      }));
      await api.post(`/stock-items/area/${selectedArea}/reorder`, { items: orderData });
    } catch (err) {
      console.error('Failed to update order:', err);
      fetchAreaItems(); // Revert on error
    }
  };

  const availableItems = items.filter(
    item => !areaItems.find(ai => ai.id === item.id)
  );

  return (
    <div className="container">
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Assign Items to Stock Areas</h2>
          <button className="btn btn-secondary btn-small" onClick={() => navigate('/')}>
            Back to Dashboard
          </button>
        </div>

        {areas.length === 0 ? (
          <div className="alert alert-info">
            Please create stock areas first before assigning items.
          </div>
        ) : (
          <>
            <div className="form-group">
              <label className="form-label">Select Stock Area</label>
              <select
                className="form-select"
                value={selectedArea || ''}
                onChange={(e) => setSelectedArea(Number(e.target.value))}
              >
                {areas.map(area => (
                  <option key={area.id} value={area.id}>{area.name}</option>
                ))}
              </select>
            </div>

            <h3 className="mb-2">Items in {areas.find(a => a.id === selectedArea)?.name}</h3>
            <p style={{ color: 'var(--text-light)', marginBottom: '1rem' }}>
              Drag items to reorder them
            </p>

            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="area-items">
                {(provided) => (
                  <div {...provided.droppableProps} ref={provided.innerRef} className="mb-3">
                    {areaItems.length === 0 ? (
                      <div className="text-center" style={{ padding: '2rem', color: 'var(--text-light)' }}>
                        No items in this area yet
                      </div>
                    ) : (
                      areaItems.map((item, index) => (
                        <Draggable key={item.id} draggableId={String(item.id)} index={index}>
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              className={`list-item ${snapshot.isDragging ? 'list-item-dragging' : ''}`}
                            >
                              <div className="list-item-content">
                                <div className="list-item-title">{item.name}</div>
                                <div className="list-item-subtitle">
                                  {item.code && `Code: ${item.code} • `}
                                  Type: {item.unit_type}
                                </div>
                              </div>
                              <button
                                className="btn btn-danger btn-small"
                                onClick={() => handleRemoveItemFromArea(item.id)}
                              >
                                Remove
                              </button>
                            </div>
                          )}
                        </Draggable>
                      ))
                    )}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>

            {availableItems.length > 0 && (
              <>
                <h3 className="mb-2">Available Items to Add</h3>
                <div style={{ position: 'relative', marginBottom: '1rem' }}>
                  <input
                    type="text"
                    placeholder="🔍 Search items by name or code..."
                    className="input"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{ 
                      paddingLeft: '0.75rem',
                      fontSize: '0.95rem',
                      border: '2px solid #ffb3d9',
                      borderRadius: '8px'
                    }}
                  />
                </div>
                {availableItems.filter(item => 
                  searchTerm === '' || 
                  item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  (item.code && item.code.toLowerCase().includes(searchTerm.toLowerCase()))
                ).map(item => (
                  <div key={item.id} className="list-item">
                    <div className="list-item-content">
                      <div className="list-item-title">{item.name}</div>
                      <div className="list-item-subtitle">
                        {item.code && `Code: ${item.code} • `}
                        Type: {item.unit_type}
                      </div>
                    </div>
                    <button
                      className="btn btn-primary btn-small"
                      onClick={() => handleAddItemToArea(item.id)}
                      style={{ whiteSpace: 'nowrap', width: 'auto', flex: '0 0 auto' }}
                    >
                      Add to Area
                    </button>
                  </div>
                ))}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ManageAreaItems;
